import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

// Parse a YYYY-MM-DD-like string as a LOCAL date (not UTC), so a Postgres DATE column
// value of 2026-09-06 doesn't shift to Sep 5 in PT timezones. If we just `new Date(d)`,
// JS treats YYYY-MM-DD as UTC midnight, then toLocaleDateString converts back to local
// and the rendered day slips by one.
const toLocalDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d.slice(0, 10) : '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    return new Date(y, m - 1, day);
  }
  const fallback = new Date(d);
  return isNaN(fallback.getTime()) ? null : fallback;
};

const fmtDate = (d) => {
  const date = toLocalDate(d);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtDateRange = (from, to) => {
  if (from === to) return fmtDate(from);
  const f = toLocalDate(from);
  const t = toLocalDate(to);
  if (!f || !t) return `${fmtDate(from)} – ${fmtDate(to)}`;
  if (f.getFullYear() === t.getFullYear()) {
    return `${f.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return `${fmtDate(from)} – ${fmtDate(to)}`;
};

const STATUS_COLORS = {
  pending:             { bg: '#FFFBEB', fg: '#B45309', dot: '#F59E0B' },
  approved:            { bg: '#ECFDF5', fg: '#047857', dot: '#10B981' },
  partially_approved:  { bg: '#EFF6FF', fg: '#1D4ED8', dot: '#3B82F6' },
  changes_requested:   { bg: '#FFF7ED', fg: '#9A3412', dot: '#EA6F2E' },
  denied:              { bg: '#FEF2F2', fg: '#B91C1C', dot: '#EF4444' },
};

// Sum hours per leave type across a request's allocations → [{ type, hours }].
const summarizeTypes = (allocations = []) => {
  const byType = {};
  allocations.forEach(a => { if (a.leave_type) byType[a.leave_type] = (byType[a.leave_type] || 0) + (parseFloat(a.hours) || 0); });
  return Object.entries(byType).map(([type, hours]) => ({ type, hours }));
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function TimeOffReport() {
  const [requests, setRequests] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterFrom, setFilterFrom]                 = useState('');
  const [filterTo, setFilterTo]                     = useState('');
  const [filterEmployeeId, setFilterEmployeeId]     = useState('');
  const [filterSupervisorId, setFilterSupervisorId] = useState('');
  const [filterStatus, setFilterStatus]             = useState('');
  const [filterLeaveType, setFilterLeaveType]       = useState('');

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterFrom)         params.set('from', filterFrom);
      if (filterTo)           params.set('to', filterTo);
      if (filterEmployeeId)   params.set('employee_id', filterEmployeeId);
      if (filterSupervisorId) params.set('supervisor_id', filterSupervisorId);
      if (filterStatus)       params.set('status', filterStatus);
      if (filterLeaveType)    params.set('leave_type', filterLeaveType);

      const url = `${API}/timeoff/report${params.toString() ? '?' + params.toString() : ''}`;
      const res = await axios.get(url, { headers: headers() });
      setRequests(res.data.requests || []);
      setError('');
    } catch (e) {
      if (e.response?.status === 403) {
        setError('You are not authorized to view this report.');
      } else {
        setError(e.response?.data?.error || 'Failed to load report.');
      }
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch + load staff for filter dropdowns
    fetchReport();
    axios.get(API + '/staff', { headers: headers() })
      .then(res => setStaff(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStaff([]));
  }, []);

  const onApplyFilters = (e) => { if (e) e.preventDefault(); fetchReport(); };
  const onClearFilters = () => {
    setFilterFrom(''); setFilterTo(''); setFilterEmployeeId('');
    setFilterSupervisorId(''); setFilterStatus(''); setFilterLeaveType('');
    setTimeout(fetchReport, 0); // fetch after state flush
  };

  const leaveTypes = useMemo(() => {
    const set = new Set();
    requests.forEach(r => (r.allocations || []).forEach(a => a.leave_type && set.add(a.leave_type)));
    return [...set].sort();
  }, [requests]);

  const totalHours = useMemo(() => {
    return requests.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
  }, [requests]);

  const exportCSV = () => {
    const headerRow = ['Requester','Email','Supervisor','Dates','Hours','Leave Types','Status','Submitted','Approved/Denied At','Comments'];
    const rows = requests.map(r => [
      `${r.employee_first || ''} ${r.employee_last || ''}`.trim(),
      r.employee_email || '',
      r.supervisor_name || '',
      fmtDateRange(r.date_from, r.date_to),
      r.total_hours || '',
      (r.allocations || []).map(a => `${a.leave_type}:${a.hours}`).join('; '),
      r.status || '',
      r.created_at ? new Date(r.created_at).toLocaleString() : '',
      r.supervisor_approved_at ? new Date(r.supervisor_approved_at).toLocaleString() : '',
      r.supervisor_comments || '',
    ]);
    const csv = [headerRow, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caam-time-off-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Styles
  const card = { background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '20px 24px', marginBottom: '16px' };
  const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--caam-border)', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--caam-text)', margin: 0, marginBottom: '4px' }}>Time Off Report</h2>
            <p style={{ fontSize: '12px', color: 'var(--caam-text-muted)', margin: 0 }}>
              Org-wide time-off requests with filters and CSV export. Visible to admins, Jon Lamirault, and Steven Foley.
            </p>
          </div>
          <button
            onClick={exportCSV}
            disabled={requests.length === 0}
            style={{
              padding: '8px 14px',
              background: requests.length === 0 ? '#E5E7EB' : 'var(--caam-accent)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: requests.length === 0 ? 'default' : 'pointer'
            }}
          >
            Export CSV ({requests.length})
          </button>
        </div>
      </div>

      <form onSubmit={onApplyFilters} style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          <div>
            <label style={labelStyle}>From</label>
            <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Employee</label>
            <select value={filterEmployeeId} onChange={e => setFilterEmployeeId(e.target.value)} style={inputStyle}>
              <option value="">All employees</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Supervisor</label>
            <select value={filterSupervisorId} onChange={e => setFilterSupervisorId(e.target.value)} style={inputStyle}>
              <option value="">All supervisors</option>
              {staff.filter(s => s.is_supervisor).map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputStyle}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="partially_approved">Partially Approved</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="denied">Denied</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Leave Type</label>
            <select value={filterLeaveType} onChange={e => setFilterLeaveType(e.target.value)} style={inputStyle}>
              <option value="">All types</option>
              {leaveTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button type="submit" style={{ padding: '8px 16px', background: 'var(--caam-accent)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            Apply Filters
          </button>
          <button type="button" onClick={onClearFilters} style={{ padding: '8px 16px', background: '#F3F4F6', color: 'var(--caam-text)', border: '1px solid var(--caam-border)', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
            Clear
          </button>
        </div>
      </form>

      <div style={card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--caam-text-muted)' }}>Loading report…</div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#9B3A1F' }}>{error}</div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--caam-text-muted)' }}>No requests match the current filters.</div>
        ) : (
          <>
            <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--caam-text-muted)' }}>
              <strong>{requests.length}</strong> request{requests.length !== 1 ? 's' : ''} · <strong>{totalHours.toFixed(1)}</strong> total hours
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--caam-border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Requester</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Supervisor</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Dates</th>
                    <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Hours</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Types</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.04em' }}>Acted On</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => {
                    const sc = STATUS_COLORS[r.status] || { bg: '#F3F4F6', fg: 'var(--caam-text)' };
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px' }}>
                          <div style={{ fontWeight: 500 }}>{r.employee_first} {r.employee_last}</div>
                          <div style={{ fontSize: '11px', color: 'var(--caam-text-muted)' }}>{r.employee_email}</div>
                        </td>
                        <td style={{ padding: '10px' }}>{r.supervisor_name || '—'}</td>
                        <td style={{ padding: '10px' }}>{fmtDateRange(r.date_from, r.date_to)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {r.status === 'partially_approved' && r.approved_hours != null
                            ? `${r.approved_hours} / ${r.total_hours}`
                            : (r.total_hours || '—')}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {summarizeTypes(r.allocations).map(t => (
                              <span key={t.type} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 8px', borderRadius: '6px', background: '#F3F4F6', fontSize: '11px', color: '#374151', fontWeight: 500 }}>
                                {t.type}<span style={{ color: 'var(--caam-accent)', fontWeight: 600 }}>{t.hours}h</span>
                              </span>
                            ))}
                            {(!r.allocations || r.allocations.length === 0) && <span style={{ fontSize: '12px', color: 'var(--caam-text-muted)' }}>—</span>}
                          </div>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: sc.bg, color: sc.fg, padding: '3px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.dot || '#9CA3AF' }} />
                            {(r.status || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px', color: 'var(--caam-text-muted)' }}>
                          {r.supervisor_approved_at ? new Date(r.supervisor_approved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
